import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { BrowserRouter as Router } from 'react-router-dom';

import { ThemeProvider } from 'styled-components';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { SnackbarProvider, closeSnackbar } from 'notistack';
import { IconButton } from '@mui/material';
import { X as CloseIcon } from 'lucide-react';

import createCustomTheme from './theme';
import Routes from './routes/Routes';
import type { AppStateType } from 'reducers';
import AuthenticationLayer from 'containers/AuthenticationLayer';
import SecurityLayer from 'containers/SecurityLayer';
import { createGlobalStyle } from 'styled-components';
import { initModules } from './modules/ThirdPartyModule';
import { AppProvider } from 'containers/AppProvider';
import { NotificationServiceProvider } from 'components/NotificationServiceProvider';
import { ErrorHandlerInitializer } from 'components/ErrorHandlerInitializer';
import UnauthorizedModal from 'components/UnauthorizedModal';
import { UrlContextSynchronizer } from 'components/UrlContextSynchronizer';

const GlobalStyle = createGlobalStyle<{ theme: any }>`
  html,
  body {
    height: 100%;
    margin: 0;
    padding: 0;
    background: transparent;
  }

  body {
    min-height: 100vh;
  }

  #root {
    min-height: 100%;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    background: ${(props) =>
      props.theme.palette.mode === 'dark'
        ? 'linear-gradient(135deg, #0d1520 0%, #1a2332 50%, #0f1924 100%)'
        : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 50%, #f1f3f5 100%)'};
    background-attachment: fixed;
  }

  /* Disable React Query DevTools hover effects */
  [data-query-devtools-hover] {
    outline: none !important;
    background: transparent !important;
  }

  /* Only make specific Papers transparent, not all of them */
  .MuiPaper-root.transparent-bg {
    background-color: transparent !important;
  }

  /* Fix MenuItem hover states */
  .MuiMenuItem-root:hover {
    background-color: ${(props) =>
      props.theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(0, 0, 0, 0.04)'} !important;
  }

  /* Fix Menu Paper backgrounds */
  .MuiMenu-paper {
    background-color: ${(props) =>
      props.theme.palette.mode === 'dark' ? '#1a1a1a' : '#ffffff'} !important;
  }

  /* Add subtle glow to Paper components (cards, modals, etc.) */
  .MuiPaper-root:not(.MuiMenu-paper):not(.MuiDrawer-paper) {
    box-shadow: ${(props) =>
      props.theme.palette.mode === 'dark'
        ? '0 4px 20px rgba(0, 0, 0, 0.4), 0 0 50px rgba(251, 140, 0, 0.08), 0 0 100px rgba(251, 140, 0, 0.04)'
        : '0 2px 12px rgba(0, 0, 0, 0.08)'} !important;
  }

  /* Enhanced glow for elevated cards */
  .MuiPaper-elevation1,
  .MuiPaper-elevation2,
  .MuiPaper-elevation3 {
    ${(props) =>
      props.theme.palette.mode === 'dark' &&
      `
      box-shadow: 
        0 8px 24px rgba(0, 0, 0, 0.5), 
        0 0 60px rgba(251, 140, 0, 0.12),
        0 0 120px rgba(251, 140, 0, 0.06),
        inset 0 1px 0 rgba(255, 255, 255, 0.06) !important;
      border: 1px solid rgba(255, 255, 255, 0.08);
    `}
  }  /* Notistack: Make stacked snackbars readable with solid backgrounds */
  .SnackbarItem-variantSuccess .SnackbarContent-root,
  .SnackbarItem-variantError .SnackbarContent-root,
  .SnackbarItem-variantInfo .SnackbarContent-root,
  .SnackbarItem-variantWarning .SnackbarContent-root,
  .SnackbarItem-variantSuccess .MuiPaper-root,
  .SnackbarItem-variantError .MuiPaper-root,
  .SnackbarItem-variantInfo .MuiPaper-root,
  .SnackbarItem-variantWarning .MuiPaper-root,
  .notistack-MuiContent,
  .notistack-MuiContent-success,
  .notistack-MuiContent-error,
  .notistack-MuiContent-info,
  .notistack-MuiContent-warning {
    background-color: ${(props) =>
      props.theme.palette.mode === 'dark' ? 'rgba(22, 27, 34, 0.98)' : '#ffffff'} !important;
    background-image: none !important;
    backdrop-filter: none !important;
    box-shadow: ${(props) =>
      props.theme.palette.mode === 'dark'
        ? '0 8px 24px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)'
        : '0 6px 16px rgba(0,0,0,0.12)'} !important;
    border: ${(props) =>
      props.theme.palette.mode === 'dark'
        ? '1px solid rgba(255,255,255,0.08)'
        : '1px solid rgba(0,0,0,0.08)'};
    color: ${(props) => (props.theme.palette.mode === 'dark' ? '#ffffff' : '#1a1a1a')} !important;
  }

  /* Ensure ALL notification text elements are readable in both themes */
  .notistack-MuiContent-success,
  .notistack-MuiContent-error,
  .notistack-MuiContent-info,
  .notistack-MuiContent-warning,
  .notistack-MuiContent-success *,
  .notistack-MuiContent-error *,
  .notistack-MuiContent-info *,
  .notistack-MuiContent-warning *,
  .notistack-MuiContent *,
  .SnackbarItem-message,
  .SnackbarItem-message * {
    color: ${(props) => (props.theme.palette.mode === 'dark' ? '#ffffff' : '#1a1a1a')} !important;
  }

  /* Notification icons should also be visible */
  .notistack-MuiContent svg,
  .notistack-MuiContent-success svg,
  .notistack-MuiContent-error svg,
  .notistack-MuiContent-info svg,
  .notistack-MuiContent-warning svg {
    color: ${(props) => (props.theme.palette.mode === 'dark' ? '#ffffff' : '#1a1a1a')} !important;
    fill: currentColor !important;
  }

  /* Variant-specific accent colors for better visual distinction */
  .notistack-MuiContent-success {
    border-left: 4px solid #4caf50 !important;
  }
  .notistack-MuiContent-error {
    border-left: 4px solid #f44336 !important;
  }
  .notistack-MuiContent-info {
    border-left: 4px solid #2196f3 !important;
  }
  .notistack-MuiContent-warning {
    border-left: 4px solid #ff9800 !important;
  }

  /* Small padding tweak for warning to match others */
  .SnackbarItem-variantWarning { padding: 0 !important; }

  .no-text-overflow {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .mw-10vw {
    max-width: 10vw;
  }

  /* ========================================================================
   * PAGE ANIMATIONS - Subtle entrance animations for all pages
   * ======================================================================== */
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes slideInFromRight {
    from {
      opacity: 0;
      transform: translateX(30px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes scaleIn {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  /* Apply page entrance animation */
  [data-page-container] {
    animation: fadeInUp 0.4s ease-out;
  }

  /* Stagger children animations */
  [data-page-container] > * {
    animation: fadeIn 0.5s ease-out;
    animation-fill-mode: both;
  }

  [data-page-container] > *:nth-child(1) {
    animation-delay: 0.05s;
  }

  [data-page-container] > *:nth-child(2) {
    animation-delay: 0.1s;
  }

  [data-page-container] > *:nth-child(3) {
    animation-delay: 0.15s;
  }

  [data-page-container] > *:nth-child(4) {
    animation-delay: 0.2s;
  }

  [data-page-container] > *:nth-child(5) {
    animation-delay: 0.25s;
  }

  /* Card entrance animations */
  .MuiCard-root,
  .MuiPaper-root:not(.MuiMenu-paper):not(.MuiDrawer-paper) {
    animation: scaleIn 0.3s ease-out;
    animation-fill-mode: both;
  }

  /* Respect reduced motion preferences */
  @media (prefers-reduced-motion: reduce) {
    [data-page-container],
    [data-page-container] > *,
    .MuiCard-root,
    .MuiPaper-root {
      animation: none !important;
    }
  }
`;

function App() {
  const theme = useSelector((state: AppStateType) => state.themeReducer);
  const customTheme = useMemo(() => createCustomTheme(theme.currentTheme), [theme.currentTheme]);
  initModules();

  return (
    <Router>
      {/* Sync URL params (tenant, realmId) to global store for non-React code */}
      <UrlContextSynchronizer />
      {/* Theme providers MUST be before AuthenticationLayer since it uses styled-components with theme */}
      <MuiThemeProvider theme={customTheme}>
        <ThemeProvider theme={customTheme}>
          <GlobalStyle theme={customTheme} />
          <SnackbarProvider
            maxSnack={3}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            autoHideDuration={4000}
            preventDuplicate
            action={(snackbarKey) => (
              <IconButton
                size="small"
                onClick={() => closeSnackbar(snackbarKey)}
                sx={{ color: 'inherit', opacity: 0.7 }}
                aria-label="Dismiss notification"
              >
                <CloseIcon size={18} />
              </IconButton>
            )}
          >
            <NotificationServiceProvider>
              <ErrorHandlerInitializer />
              <AuthenticationLayer>
                <SecurityLayer>
                  <HelmetProvider>
                    <Helmet
                      titleTemplate="%s | Mosaic Galaxy"
                      defaultTitle="Mosaic Galaxy - Integration Monitoring"
                    />
                    <AppProvider>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <UnauthorizedModal />
                        <Routes />
                      </LocalizationProvider>
                    </AppProvider>
                  </HelmetProvider>
                </SecurityLayer>
              </AuthenticationLayer>
            </NotificationServiceProvider>
          </SnackbarProvider>
        </ThemeProvider>
      </MuiThemeProvider>
    </Router>
  );
}

export default App;
